import { useMemo } from "react";
import {
  getArgentinaProvinceCodeByName,
  getArgentinaProvinceConfig,
  getTemplateForArgentinaProvince,
  type ArgentinaProvinceConfig,
} from "@/config/argentinaProvinces";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";

/**
 * Config de template/analytics por provincia AR — derivada de la VERDAD ÚNICA
 * de location: el Worker (__TEMPLATE_CONFIG__ / cookies → useTemplateSelection).
 *
 * Este hook NO detecta nada por su cuenta. La detección anterior (timezone +
 * IP services client-side) era una verdad paralela que contradecía al Worker:
 * el timezone de Buenos Aires cubre CABA y media Argentina, así que casi todo
 * visitante AR caía en "BA" — incluida CABA, que es una provincia distinta.
 * El Worker resuelve provincia con CF region code + IPLocate y la inyecta en
 * el HTML; acá solo se mapea nombre → provinceCode del catálogo.
 */
export const useArgentinaTemplate = () => {
  const { country, province } = useTemplateSelection();

  const { provinceCode, provinceConfig, isLoading } = useMemo(() => {
    if (typeof window === "undefined") {
      return { provinceCode: null as string | null, provinceConfig: null as ArgentinaProvinceConfig | null, isLoading: false };
    }

    // Override de test (solo dev) — se mantiene para QA manual de templates.
    const testProvince = import.meta.env.DEV
      ? localStorage.getItem("test_argentina_province")
      : null;
    if (testProvince) {
      return {
        provinceCode: testProvince,
        provinceConfig: getArgentinaProvinceConfig(testProvince),
        isLoading: false,
      };
    }

    if (country !== "AR") {
      return { provinceCode: null, provinceConfig: null, isLoading: false };
    }
    if (!province) {
      // AR sin provincia resuelta por el Worker (raro): sin config específica.
      return { provinceCode: "AR_UNKNOWN", provinceConfig: null, isLoading: false };
    }
    const code = getArgentinaProvinceCodeByName(province);
    return {
      provinceCode: code ?? "AR_UNKNOWN",
      provinceConfig: code ? getArgentinaProvinceConfig(code) : null,
      isLoading: false,
    };
  }, [country, province]);

  const getTemplateForCurrentProvince = (): "BaseTemplate" | "BioLinkTemplate" => {
    if (provinceCode && provinceCode !== "AR_UNKNOWN") {
      return getTemplateForArgentinaProvince(provinceCode);
    }
    // Default to BaseTemplate for unknown provinces (more accessible)
    return "BaseTemplate";
  };

  const shouldUseBaseTemplate = (): boolean => {
    return getTemplateForCurrentProvince() === "BaseTemplate";
  };

  const shouldUseBioLinkTemplate = (): boolean => {
    return getTemplateForCurrentProvince() === "BioLinkTemplate";
  };

  const isHighIncomeProvince = (): boolean => {
    return provinceConfig?.economicLevel === "high" || false;
  };

  const getProvinceRegion = (): string | null => {
    return provinceConfig?.region || null;
  };

  const getProvinceName = (): string | null => {
    return provinceConfig?.provinceName || null;
  };

  // Development helper functions
  const setTestProvince = (testProvinceCode: string) => {
    if (import.meta.env.DEV) {
      localStorage.setItem("test_argentina_province", testProvinceCode);
      window.location.reload(); // Reload to apply changes
    }
  };

  const clearTestProvince = () => {
    if (import.meta.env.DEV) {
      localStorage.removeItem("test_argentina_province");
      window.location.reload(); // Reload to apply changes
    }
  };

  return {
    // Province data
    provinceCode,
    provinceConfig,
    isLoading,

    // Template selection
    templateName: getTemplateForCurrentProvince(),
    shouldUseBaseTemplate,
    shouldUseBioLinkTemplate,

    // Province information
    isHighIncomeProvince,
    getProvinceRegion,
    getProvinceName,

    // Development helpers
    setTestProvince,
    clearTestProvince,
  };
};
